import { useEffect, useState } from "react";
import { useMutation, useRoom, useStorage } from "@liveblocks/react/suspense";
import {
  computed,
  createPresenceStateDerivation,
  createTLStore,
  react,
  defaultShapeUtils,
  DocumentRecordType,
  InstancePresenceRecordType,
  PageRecordType,
  IndexKey,
  TLAnyShapeUtilConstructor,
  TLAsset,
  TLDocument,
  TLInstancePresence,
  TLPageId,
  TLRecord,
  TLStoreEventInfo,
  TLStoreWithStatus,
} from "@tldraw/tldraw";
import { MyCustomShapes } from "pages/flyer/builder/drawer";
import { LiveMap, LiveObject } from "@liveblocks/core";
import { getSignedUrl } from "graphQL/query/file";
import { readS3Marker } from "pages/flyer/builder/utils/s3Marker";

// Cache signed URLs to avoid re-signing on every render cycle
const signedUrlCache = new Map<string, { url: string; expiry: number }>();

export function useStorageStore({
  shapeUtils = [],
  user,
}: Partial<{
  hostUrl: string;
  version: number;
  shapeUtils: TLAnyShapeUtilConstructor[];
  user: {
    id: string;
    color: string;
    name: string;
  };
}>) {
  // Get Liveblocks room
  const room = useRoom();

  // Set up tldraw store and status - use useState to preserve instance across re-renders
  const [store] = useState(() =>
    createTLStore({
      // @ts-ignore
      shapeUtils: [...defaultShapeUtils, ...shapeUtils, ...MyCustomShapes],
      assets: {
        async upload(_asset: TLAsset, _file: File) {
          // Upload is handled by useMultiplayerAssets / onAssetCreate
          throw new Error("Asset upload not configured via store");
        },
        async resolve(asset: TLAsset) {
          const src = (asset.props as any)?.src as string | undefined;
          if (!src) return null;

          // Skip data URLs and non-http URLs
          if (!src.startsWith("http")) return src;

          // 1. Check for #s3key= marker (new-style URLs from useMultiplayerAssets / PDFImport)
          if (src.includes("#s3key=") || src.includes("#s3bucket=")) {
            try {
              const marker = readS3Marker(src);
              if (!marker) return src;
              const { bucket, key, region } = marker;

              // Use a simple in-memory cache to avoid re-signing on every render
              const cacheKey = `${bucket}/${key}`;
              const cached = signedUrlCache.get(cacheKey);
              if (cached && cached.expiry > Date.now()) {
                return cached.url;
              }

              const signedUrl = await getSignedUrl(bucket, key, "GET", region);
              // Cache for 4 minutes (signed URLs typically expire in 5 min)
              signedUrlCache.set(cacheKey, { url: signedUrl, expiry: Date.now() + 4 * 60 * 1000 });
              return signedUrl;
            } catch (error) {
              console.error("❌ [assetStore.resolve] Error re-signing S3 URL:", error);
              return src.split("#")[0]; // Return URL without fragment as fallback
            }
          }

          // 2. Fallback: detect raw S3 URLs (legacy designs without #s3key= marker)
          const s3Match = src.match(/^https?:\/\/([^"'\s/?]+)\.s3(?:\.([^"'\s/.]+))?\.amazonaws\.com\/([^"'\s?#]+)/);
          if (s3Match) {
            try {
              const bucket = s3Match[1];
              const region = s3Match[2] || "us-east-1";
              const key = decodeURIComponent(s3Match[3]);

              const cacheKey = `${bucket}/${key}`;
              const cached = signedUrlCache.get(cacheKey);
              if (cached && cached.expiry > Date.now()) {
                return cached.url;
              }

              const signedUrl = await getSignedUrl(bucket, key, "GET", region);
              signedUrlCache.set(cacheKey, { url: signedUrl, expiry: Date.now() + 4 * 60 * 1000 });
              return signedUrl;
            } catch (error) {
              console.error("❌ [assetStore.resolve] Error signing legacy S3 URL:", error);
              return src;
            }
          }

          // Not an S3 URL, return as-is
          return src;
        },
      },
    })
  );

  const [storeWithStatus, setStoreWithStatus] = useState<TLStoreWithStatus>({
    status: "loading",
  });

  // Sync tldraw changes with Presence
  function syncStoreWithPresence({ changes }: TLStoreEventInfo) {
    room.batch(() => {
      Object.values(changes.added).forEach((record: any) => {
        room.updatePresence({ [record.id]: record });
      });

      Object.values(changes.updated).forEach(([_, record]: any) => {
        room.updatePresence({ [record.id]: record });
      });

      Object.values(changes.removed).forEach((record) => {
        room.updatePresence({ [record.id]: null });
      });
    });
  }

  const setup = async () => {
    if (!room) return;
    const storage = await room.getStorage();
    try {
      const unsubs: (() => void)[] = [];
      // Get Liveblocks Storage values
      let liveRecords = storage.root.get("records") as any;

      // Detect if this is a new design
      const urlParams = new URLSearchParams(window.location.search);
      const isNewDesign = urlParams.get("newDesign") === "true" || urlParams.get("action") === "create";

      const recordCount = liveRecords?.size ?? 0;

      // If this is a new design but the room has pre-existing data (stale data from a previous
      // design that reused the same room key), clear it to prevent ghost content
      if (isNewDesign && recordCount > 0) {
        console.warn(`[LB-CLEAN] New design in room "${room.id}" has ${recordCount} stale records — clearing`);
        // Clear all existing records
        const keys = [...(liveRecords.keys() ?? [])];
        keys.forEach((key: string) => liveRecords.delete(key));
      }

      if (!liveRecords?.size) {
        liveRecords = new LiveMap();
        storage.root.set("records", liveRecords);
      }

      // Initialize tldraw with records from Storage
      store.clear();
      store.put(
        [
          DocumentRecordType.create({
            id: "document:document" as TLDocument["id"],
          }),
          PageRecordType.create({
            id: "page:page" as TLPageId,
            name: "Page 1",
            index: "a1" as IndexKey,
          }),
          // @ts-ignore
          ...[...(liveRecords?.values() ?? [])],
        ],
        "initialize"
      );

      // Sync tldraw changes with Storage
      unsubs.push(
        store.listen(
          ({ changes }: TLStoreEventInfo) => {
            room.batch(() => {
              Object.values(changes.added).forEach((record) => {
                liveRecords.set(record.id, record as any);
              });

              Object.values(changes.updated).forEach(([_, record]) => {
                liveRecords.set(record.id, record as any);
              });

              Object.values(changes.removed).forEach((record) => {
                liveRecords.delete(record.id);
              });
            });
          },
          { source: "user", scope: "document" }
        )
      );

      unsubs.push(
        store.listen(syncStoreWithPresence, {
          source: "user",
          scope: "session",
        })
      );

      unsubs.push(
        store.listen(syncStoreWithPresence, {
          source: "user",
          scope: "presence",
        })
      );

      // Update tldraw when Storage changes
      unsubs.push(
        room.subscribe(
          liveRecords,
          (storageChanges) => {
            const toRemove: TLRecord["id"][] = [];
            const toPut: TLRecord[] = [];

            for (const update of storageChanges) {
              if (update.type !== "LiveMap") {
                return;
              }

              for (const [id, { type }] of Object.entries(update.updates)) {
                switch (type) {
                  // Object deleted from Liveblocks, remove from tldraw
                  case "delete": {
                    toRemove.push(id as TLRecord["id"]);
                    break;
                  }

                  // Object updated on Liveblocks, update tldraw
                  case "update": {
                    const curr = update.node.get(id);
                    if (curr) {
                      toPut.push(curr as any as TLRecord);
                    }
                    break;
                  }
                }
              }
            }

            // Update tldraw with changes
            store.mergeRemoteChanges(() => {
              if (toRemove.length) {
                store.remove(toRemove);
              }
              if (toPut.length) {
                store.put(toPut);
              }
            });
          },
          { isDeep: true }
        )
      );

      // Set user's info
      const userPreferences = computed<{
        id: string;
        color: string;
        name: string;
      }>("userPreferences", () => {
        if (!user) {
          throw new Error("Failed to get user");
        }
        return {
          id: user.id,
          color: user.color,
          name: user.name,
        };
      });

      // Unique ID for this session is their connectionId
      const connectionIdString = "" + (room.getSelf()?.connectionId || 0);

      // Set both
      const presenceDerivation = createPresenceStateDerivation(
        userPreferences,
        InstancePresenceRecordType.createId(connectionIdString)
      )(store);

      // Update presence with tldraw values
      room.updatePresence({
        presence: presenceDerivation.get() ?? (null as any),
      });

      // Update Liveblocks when tldraw presence changes
      unsubs.push(
        react("when presence changes", () => {
          const presence = presenceDerivation.get() ?? null;
          requestAnimationFrame(() => {
            room.updatePresence({ presence: presence as any });
          });
        })
      );

      // Sync Liveblocks presence with tldraw
      unsubs.push(
        room.subscribe("others", (others, event) => {
          const toRemove: TLInstancePresence["id"][] = [];
          const toPut: TLInstancePresence[] = [];

          switch (event.type) {
            // A user disconnected from Liveblocks
            case "leave": {
              if (event.user.connectionId) {
                toRemove.push(InstancePresenceRecordType.createId(`${event.user.connectionId}`));
              }
              break;
            }

            // Others was reset, e.g. after losing connection and returning
            case "reset": {
              others.forEach((other) => {
                toRemove.push(InstancePresenceRecordType.createId(`${other.connectionId}`));
              });
              break;
            }

            // A user entered or their presence updated
            case "enter":
            case "update": {
              const presence = event?.user?.presence;
              if (presence?.presence) {
                // Strip the remote user's selection from the presence we
                // hand to tldraw. With the default Liveblocks/tldraw bridge,
                // when the local store doesn't have the shape ids the remote
                // is selecting (initial sync race, or a shape that hasn't
                // arrived yet) tldraw renders the selection rect at a
                // fallback position — which shows up as a phantom selector
                // floating near the center of the canvas. We don't need
                // remote selection rectangles for our UX (just cursors), so
                // the cleanest fix is to drop them entirely.
                const remotePresence = { ...(event.user.presence.presence as any) };
                remotePresence.selectedShapeIds = [];
                toPut.push(remotePresence as any);
              }
            }
          }

          // Update tldraw with changes
          store.mergeRemoteChanges(() => {
            if (toRemove.length) {
              store.remove(toRemove);
            }
            if (toPut.length) {
              store.put(toPut);
            }
          });
        })
      );

      setStoreWithStatus({
        store,
        status: "synced-remote",
        connectionStatus: "online",
      });

      return unsubs;
    } catch (error) {
      console.error("Error initializing storage store:", error);
      return [];
    } finally {
      setStoreWithStatus({
        store,
        status: "synced-remote",
        connectionStatus: "online",
      });
    }
  };

  useEffect(() => {
    setStoreWithStatus({ status: "loading" });
    const promise = setup();
    return () => {
      promise.then((unsubs) => {
        if (unsubs) {
          unsubs.forEach((fn) => fn());
          unsubs.length = 0;
        }
      });
    };
  }, [room]);

  return storeWithStatus;
}
